import { CSSProperties, memo } from 'react';
import { useTranslation } from 'react-i18next';
import numbro from 'numbro';

import {
  CurrencyType,
  getCurrencyDetails,
  SLICE_PER_BREAD,
} from '../../../constants/currencies';
import { PNGIconName } from '../../../constants/pngIcon';
import { SVGIconType } from '../../../constants/svgIcon';
import { BalanceType } from '../../../models/Base';
import { PngIcon } from '../PngIcon';
import { ProgressBar } from '../ProgressBar';
import { SvgIcon } from '../SVGIcon';

import styles from './CurrencyNode.module.css';

export type CurrencyNodeProps = {
  currencyType: CurrencyType;
  amount: number;
  fromType?: CurrencyType;
  fromAmount?: number;
  isPaying?: boolean;
  withBox?: boolean;
  iconSize?: number;
  textStyle?: CSSProperties;
};

const slicePerBread = SLICE_PER_BREAD;

const CurrencyItem = ({
  currencyType,
  amount,
  icon,
  iconSize,
  textStyle,
}: {
  currencyType: CurrencyType;
  amount: number;
  icon: PNGIconName;
  iconSize?: number;
  textStyle?: CSSProperties;
}) => {
  let realAmount = amount;
  let slice: number | undefined;
  if (currencyType === BalanceType.BreadSlice) {
    slice = amount % slicePerBread;
    realAmount = (amount - slice) / slicePerBread;
  }

  return (
    <div className={styles.currencyItem}>
      <div className={styles.icon}>
        <PngIcon name={icon} width={iconSize} height={iconSize} />
        {slice !== undefined && (
          <div className={styles.breadBar}>
            <ProgressBar
              current={slice}
              total={slicePerBread}
              width={`${iconSize ?? 24}px`}
              height="6px"
              borderRadius="3px"
              backgroundColor="#F0F2F5"
              fillColor="#EB8C36"
            />
          </div>
        )}
      </div>
      <span style={textStyle}>
        {numbro(realAmount).format({ thousandSeparated: true })}
      </span>
    </div>
  );
};

export const CurrencyNode = memo(
  ({
    currencyType,
    amount,
    fromType,
    fromAmount = amount,
    isPaying = true,
    withBox = true,
    iconSize = 24,
    textStyle,
  }: CurrencyNodeProps) => {
    const { t } = useTranslation();
    const icon = getCurrencyDetails(currencyType).icon;
    const fromIcon = fromType ? getCurrencyDetails(fromType).icon : undefined;
    return (
      <div
        className={
          withBox ? `${styles.container} ${styles.box}` : styles.container
        }
      >
        {fromType && fromIcon ? (
          <>
            <CurrencyItem
              currencyType={fromType}
              amount={fromAmount}
              icon={fromIcon}
              iconSize={iconSize}
              textStyle={textStyle}
            />
            <SvgIcon name={SVGIconType.DirectArrow} width={24} height={24} />
          </>
        ) : isPaying ? (
          <div style={textStyle}>{t('Pay: ')}</div>
        ) : null}
        <CurrencyItem
          currencyType={currencyType}
          amount={amount}
          icon={icon}
          iconSize={iconSize}
          textStyle={textStyle}
        />
      </div>
    );
  },
);
